import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatMinutes } from "@/lib/constants";
import { Wrench, Plus, Trash2, Loader2, Check, Cpu, Rocket, X } from "lucide-react";

const inputCls = "w-full h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500 transition-colors";

function GenerateDialog({ machine, onClose }) {
  const [commessa, setCommessa] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!commessa.trim()) return toast.error("Inserisci il codice commessa.");
    setBusy(true);
    try {
      const { data } = await api.post(`/machines/${machine.id}/generate-order`, { new_commessa: commessa.trim(), date, due_date: dueDate || null });
      toast.success(`Create ${data.created_count} fasi di montaggio per ${data.commessa}.`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl" data-testid="generate-order-dialog">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-slate-200"><X className="h-5 w-5" /></button>
        <div className="flex items-center gap-2 mb-1">
          <Rocket className="h-5 w-5 text-amber-400" />
          <h3 className="font-heading text-xl font-bold">Genera commessa</h3>
        </div>
        <p className="text-sm text-slate-500 mb-5">Crea i montaggi di una nuova commessa dalle {machine.parts?.length || 0} parti della macchina <span className="text-slate-300">{machine.name}</span>.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Nuova commessa</label>
            <input data-testid="gen-commessa" className={`${inputCls} mt-2`} placeholder="COM-2026-XXX" value={commessa} onChange={(e) => setCommessa(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Data</label>
              <input type="date" className={`${inputCls} mt-2`} value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-mono-data uppercase tracking-widest text-slate-400">Scadenza</label>
              <input type="date" data-testid="gen-due" className={`${inputCls} mt-2`} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-lg border border-slate-700 text-slate-300 text-sm hover:bg-slate-800">Annulla</button>
            <button type="submit" disabled={busy} data-testid="gen-submit" className="flex-1 h-11 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Genera montaggi
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Macchine() {
  const [machines, setMachines] = useState([]);
  const [lines, setLines] = useState([]);
  const [parts, setParts] = useState([]);
  const [form, setForm] = useState({ name: "", code: "", line_id: "", part_ids: [], notes: "" });
  const [busy, setBusy] = useState(false);
  const [genMachine, setGenMachine] = useState(null);

  const load = async () => {
    const [m, l, p] = await Promise.all([api.get("/machines"), api.get("/lines"), api.get("/parts")]);
    setMachines(m.data);
    setLines(l.data);
    setParts(p.data);
  };
  useEffect(() => { load(); }, []);

  const togglePart = (id) =>
    setForm((f) => ({ ...f, part_ids: f.part_ids.includes(id) ? f.part_ids.filter((x) => x !== id) : [...f.part_ids, id] }));

  const totalStd = useMemo(
    () => form.part_ids.reduce((s, id) => s + (parts.find((p) => p.id === id)?.standard_minutes || 0), 0),
    [form.part_ids, parts]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.code || !form.line_id) return toast.error("Nome, codice e macchinario obbligatori.");
    if (form.part_ids.length === 0) return toast.error("Seleziona almeno una parte.");
    setBusy(true);
    try {
      await api.post("/machines", form);
      setForm({ name: "", code: "", line_id: "", part_ids: [], notes: "" });
      toast.success("Macchina personalizzata creata.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa macchina?")) return;
    await api.delete(`/machines/${id}`);
    toast.success("Macchina eliminata.");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Macchine Personalizzate</h1>
        <p className="text-slate-500 text-sm mt-1">Componi una macchina selezionando le parti presenti per ogni macchinario, poi genera una commessa.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6 space-y-5">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Wrench className="h-5 w-5 text-amber-400" /> Nuova macchina</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input data-testid="machine-name" className={`${inputCls} sm:col-span-1`} placeholder="Nome macchina" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="machine-code" className={inputCls} placeholder="Codice" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
            <select data-testid="machine-line" className={inputCls} value={form.line_id} onChange={(e) => setForm({ ...form, line_id: e.target.value })}>
              <option value="">Macchinario / Linea…</option>
              {lines.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <div>
            <div className="text-xs font-mono-data uppercase tracking-widest text-slate-400 mb-2">Parti / Componenti ({form.part_ids.length} selezionate)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-auto pr-1">
              {parts.map((p) => {
                const sel = form.part_ids.includes(p.id);
                return (
                  <button type="button" key={p.id} data-testid={`machine-part-${p.id}`} onClick={() => togglePart(p.id)} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${sel ? "border-amber-500/50 bg-amber-500/10" : "border-slate-800 bg-slate-950/40 hover:border-slate-600"}`}>
                    <span className={`h-5 w-5 rounded flex items-center justify-center border ${sel ? "bg-amber-500 border-amber-500" : "border-slate-600"}`}>{sel && <Check className="h-3.5 w-3.5 text-slate-950" />}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm truncate">{p.name}</span>
                      <span className="block text-xs text-slate-500 font-mono-data">{p.code} · {p.standard_minutes || 0} min</span>
                    </span>
                  </button>
                );
              })}
              {parts.length === 0 && <p className="text-slate-500 text-sm">Nessuna parte in anagrafica.</p>}
            </div>
          </div>

          <textarea data-testid="machine-notes" rows={2} className="w-full rounded-lg bg-slate-900 border border-slate-700 px-4 py-3 text-sm outline-none focus:border-amber-500 resize-none" placeholder="Note sulla macchina…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Tempo standard totale: <span className="font-mono-data text-amber-400">{formatMinutes(totalStd)}</span></span>
            <button type="submit" disabled={busy} data-testid="machine-save" className="h-11 px-5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Crea macchina
            </button>
          </div>
        </form>

        <div className="space-y-3">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Cpu className="h-4 w-4 text-amber-400" /> Macchine create</h2>
          {machines.length === 0 && <p className="text-slate-500 text-sm">Nessuna macchina ancora creata.</p>}
          {machines.map((m) => (
            <div key={m.id} data-testid={`machine-item-${m.id}`} className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold">{m.name}</div>
                  <div className="text-xs font-mono-data text-slate-500">{m.code} · {m.line_name}</div>
                </div>
                <button onClick={() => del(m.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {m.parts?.map((p) => <span key={p.id} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">{p.name}</span>)}
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-slate-500 font-mono-data">Standard totale: <span className="text-amber-400">{formatMinutes(m.total_standard_minutes)}</span></span>
                <button onClick={() => setGenMachine(m)} data-testid={`machine-generate-${m.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center gap-1.5">
                  <Rocket className="h-3.5 w-3.5" /> Genera commessa
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {genMachine && <GenerateDialog machine={genMachine} onClose={() => setGenMachine(null)} />}
    </div>
  );
}
